import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { BrandingEditor } from '../../components/account/BrandingEditor';
import { completeOnboardingRequest } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';

const DEFAULT_COLOR = '#201C35';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { initializing, isAuthenticated, isSuperAdmin, needsOnboarding, refreshUser } = useAuth();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_COLOR);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  if (initializing) {
    return <LoadingOverlay loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin || !needsOnboarding) {
    return <Navigate to="/" replace />;
  }

  function handleLogoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(file);
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (!logoFile) {
      setError('Please upload your company logo.');
      return;
    }

    setSubmitting(true);
    try {
      await completeOnboardingRequest({ logoFile, primaryColor });
      await refreshUser({ bustLogo: true });
      navigate('/', { replace: true });
    } catch (submitError) {
      setError(submitError.message || 'Unable to complete onboarding.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#EEF1F5] px-4 py-10">
      <div className="w-full max-w-lg rounded-xl border border-slate-200/90 bg-white p-8 shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
        <div className="mb-8 text-center">
          <h1 className="font-display text-2xl font-bold tracking-[-0.03em] text-[var(--brand-primary,#201C35)]">
            Welcome to Venue Intelligence
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Upload your logo and choose a brand color to personalize your dashboard.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          <BrandingEditor
            primaryColor={primaryColor}
            onPrimaryColorChange={setPrimaryColor}
            logoPreview={logoPreview}
            onLogoChange={handleLogoChange}
            disabled={submitting}
          />

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? 'Saving…' : 'Continue to dashboard'}
          </Button>
        </form>
      </div>
    </div>
  );
}
